'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'

import type { EmojiName } from '@portal/news-lib/emoji'

import { LoadingSpinner } from '@/packages/news-ui/reaction-panel/ui/spinner/view'
import type { Post } from '@/shared/newsdb'

import { getPostReactions, updateReaction } from './actions'
import { fireConfetti } from './confetti'
import { type PostReactions, postReactionsQueryKey, toPostReactions } from './query'
import { ReactionList } from './ui/reaction-list/view'
import { sortEmojiStats } from './utils/sort-emoji-stats'

type PostReactionPanelProps = {
    postId: string
    post?: Post
}

export function ReactionPanel({ postId, post }: PostReactionPanelProps) {
    const { data } = useQuery({
        queryKey: postReactionsQueryKey(postId),
        queryFn: () => getPostReactions(postId),
        // NOTE: initialData prevents getPostReactions server action call on mount for posts loaded by infinite scroll,
        // they are not in the prefetched query cache.
        initialData: post && toPostReactions(post),
        staleTime: 1000 * 60, // 1 minute
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    if (!data) {
        return <LoadingSpinner />
    }

    return (
        <React.Suspense>
            <ReactionWrapper postID={postId} stats={data.reactions} playerReaction={data.playerReaction} />
        </React.Suspense>
    )
}

type ReactionsProps = {
    postID: string
    stats: Record<EmojiName, number>
    playerReaction: EmojiName | ''
}

function ReactionWrapper({ postID, stats, playerReaction }: ReactionsProps) {
    const reactions = React.useMemo(() => sortEmojiStats(stats), [stats])

    const queryClient = useQueryClient()

    const { mutate, isPending, variables } = useMutation({
        mutationFn: updateReaction,
        onSettled: (data) => {
            if (data) {
                queryClient.setQueryData<PostReactions>(postReactionsQueryKey(postID), data)
            }
        },
    })

    const handleClick = (emoji: EmojiName, position: { x: number; y: number }) => {
        if (emoji === playerReaction) {
            mutate({ postID, reaction: '' })
        } else {
            mutate({ postID, reaction: emoji })
            void fireConfetti({ reaction: emoji, position })
        }
    }

    return (
        <aside>
            <ReactionList
                reactions={reactions}
                checkedEmojiName={isPending ? variables.reaction : playerReaction}
                onClick={handleClick}
            />
        </aside>
    )
}
